import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

export type QAStatus =
  | "draft"
  | "needs_review"
  | "needs_improvement"
  | "approved"
  | "launch_ready";

export interface LaunchQAItem {
  id: string;
  position: number;
  item_type: "lesson" | "simulator";
  ref_id: string;
  title: string;
  subtitle: string | null;
  qa_status: QAStatus;
  qa_score: number | null;
  qa_notes: string | null;
  primary_competency: string | null;
  estimated_minutes: number | null;
  parent_insight_hook: string | null;
  parent_conversation_prompt: string | null;
  latest_review?: ReviewRow | null;
}

export interface ReviewRow {
  id: string;
  launch_item_id: string;
  reviewer_id: string | null;
  review_version: number;
  score_clarity: number | null;
  score_teen_engagement: number | null;
  score_real_world: number | null;
  score_decision_quality: number | null;
  score_quiz_quality: number | null;
  score_simulation_quality: number | null;
  score_adaptive_tagging: number | null;
  score_parent_insight: number | null;
  score_instructor_voice: number | null;
  score_safety: number | null;
  score_mobile_readability: number | null;
  score_completion_time: number | null;
  score_first_session_value: number | null;
  score_beats_generic_ai: number | null;
  average_score: number | null;
  reviewer_notes: string | null;
  required_fixes: string | null;
  recommended_status: string | null;
  created_at: string;
}

export const REQUIRED_MIN_4_FIELDS: (keyof ReviewRow)[] = [
  "score_clarity",
  "score_teen_engagement",
  "score_real_world",
  "score_parent_insight",
  "score_safety",
  "score_mobile_readability",
  "score_beats_generic_ai",
];

export function canPromoteToLaunchReady(item: LaunchQAItem, review: ReviewRow | null | undefined) {
  const missing: string[] = [];
  if (!item.parent_insight_hook) missing.push("Parent insight hook");
  if (!item.parent_conversation_prompt) missing.push("Parent conversation prompt");
  if (!item.primary_competency) missing.push("Primary competency");
  if (!item.estimated_minutes) missing.push("Estimated minutes");
  if (!review) {
    missing.push("Reviewer scoring");
    return { ok: false, missing };
  }
  if (!review.reviewer_notes || review.reviewer_notes.trim().length < 10)
    missing.push("Reviewer notes (10+ chars)");
  for (const f of REQUIRED_MIN_4_FIELDS) {
    const v = review[f] as number | null;
    if (v == null || v < 4) missing.push(`${f.replace("score_", "")} ≥ 4`);
  }
  if (item.item_type === "simulator") {
    if ((review.score_simulation_quality ?? 0) < 4)
      missing.push("simulation_quality ≥ 4");
  }
  if (review.score_quiz_quality != null && review.score_quiz_quality < 4)
    missing.push("quiz_quality ≥ 4 (if scored)");
  return { ok: missing.length === 0, missing };
}

export function useLaunchQA() {
  const { user } = useAuth();
  const [items, setItems] = useState<LaunchQAItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = supabase;
      const { data: rows, error: e1 } = await client
        .from("launch_path_items")
        .select("*")
        .order("position", { ascending: true });
      if (e1) throw e1;
      const ids = (rows || []).map((r: AnyValue) => r.id);
      const { data: reviews, error: e2 } = await client
        .from("launch_path_item_reviews")
        .select("*")
        .in("launch_item_id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"])
        .order("created_at", { ascending: false });
      if (e2) throw e2;
      const latestByItem = new Map<string, ReviewRow>();
      for (const r of reviews || []) {
        if (!latestByItem.has(r.launch_item_id)) latestByItem.set(r.launch_item_id, r);
      }
      setItems(
        (rows || []).map((r: AnyValue) => ({
          ...r,
          latest_review: latestByItem.get(r.id) ?? null,
        })),
      );
    } catch (err: unknown) {
      setError(getErrorMessage(err) ?? "Failed to load Launch QA items");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveReview = useCallback(
    async (itemId: string, payload: Partial<ReviewRow>) => {
      const scoreKeys: (keyof ReviewRow)[] = [
        "score_clarity",
        "score_teen_engagement",
        "score_real_world",
        "score_decision_quality",
        "score_quiz_quality",
        "score_simulation_quality",
        "score_adaptive_tagging",
        "score_parent_insight",
        "score_instructor_voice",
        "score_safety",
        "score_mobile_readability",
        "score_completion_time",
        "score_first_session_value",
        "score_beats_generic_ai",
      ];
      const nums = scoreKeys
        .map((k) => payload[k] as number | null | undefined)
        .filter((v): v is number => typeof v === "number");
      const avg = nums.length ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2)) : null;
      const client = supabase;
      const { data, error: e } = await client
        .from("launch_path_item_reviews")
        .insert({
          launch_item_id: itemId,
          reviewer_id: user?.id ?? null,
          ...payload,
          average_score: avg,
        })
        .select()
        .single();
      if (e) throw e;
      await client
        .from("launch_path_items")
        .update({ qa_score: avg, qa_notes: payload.reviewer_notes ?? null })
        .eq("id", itemId);
      await load();
      return data as ReviewRow;
    },
    [user, load],
  );

  const changeStatus = useCallback(
    async (item: LaunchQAItem, newStatus: QAStatus, notes?: string) => {
      const allowed: Record<QAStatus, QAStatus[]> = {
        draft: ["needs_review"],
        needs_review: ["needs_improvement", "launch_ready"],
        needs_improvement: ["needs_review", "launch_ready"],
        approved: ["launch_ready", "needs_review", "needs_improvement"],
        launch_ready: ["needs_review", "needs_improvement"],
      };
      if (!allowed[item.qa_status]?.includes(newStatus)) {
        throw new Error(`Status transition ${item.qa_status} → ${newStatus} not allowed`);
      }
      if (newStatus === "launch_ready") {
        const gate = canPromoteToLaunchReady(item, item.latest_review ?? null);
        if (!gate.ok)
          throw new Error(`Cannot promote — missing: ${gate.missing.join(", ")}`);
      }
      const client = supabase;
      const { error: e1 } = await client
        .from("launch_path_items")
        .update({ qa_status: newStatus })
        .eq("id", item.id);
      if (e1) throw e1;
      await client.from("launch_path_status_history").insert({
        launch_item_id: item.id,
        previous_status: item.qa_status,
        new_status: newStatus,
        changed_by: user?.id ?? null,
        notes: notes ?? null,
      });
      await load();
    },
    [user, load],
  );

  const stats = useMemo(() => {
    const counts = {
      total: items.length,
      launch_ready: 0,
      needs_review: 0,
      needs_improvement: 0,
      approved: 0,
      avg_score: 0,
      missing_metadata: 0,
    };
    let scoreSum = 0;
    let scoreN = 0;
    for (const it of items) {
      counts[it.qa_status as keyof typeof counts] =
        ((counts[it.qa_status as keyof typeof counts] as number) ?? 0) + 1;
      if (it.qa_score != null) {
        scoreSum += Number(it.qa_score);
        scoreN++;
      }
      if (
        !it.parent_insight_hook ||
        !it.parent_conversation_prompt ||
        !it.primary_competency ||
        !it.estimated_minutes
      )
        counts.missing_metadata++;
    }
    counts.avg_score = scoreN ? Number((scoreSum / scoreN).toFixed(2)) : 0;
    return counts;
  }, [items]);

  return { items, loading, error, stats, refetch: load, saveReview, changeStatus };
}